import { useEffect, useRef } from 'react';
import { heartbeatApi, type HeartbeatPayload } from './heartbeat.api';

interface UseHeartbeatOptions {
  /** Active when there's an authenticated user. */
  enabled: boolean;
  /** If we're inside a screening, pass its id so the backend can correlate. */
  screeningId?: string;
  /** Override the default 30s pulse if needed (useful for tests). */
  intervalMs?: number;
  /** N seconds without input → state becomes 'idle'. */
  idleThresholdMs?: number;
}

/**
 * Sends operational telemetry to /agents/me/heartbeat every ~30 seconds.
 *
 * Captures COUNTS only (keystrokes, clicks) — NOT content. Tab focus is
 * tracked via Page Visibility API.
 *
 * Failures are swallowed (best-effort). The reaper job on the backend
 * handles missing heartbeats by treating the agent as disconnected.
 */
export function useHeartbeat({
  enabled,
  screeningId,
  intervalMs = 30_000,
  idleThresholdMs = 60_000,
}: UseHeartbeatOptions): void {
  // Counters that reset every flush.
  const keysRef = useRef(0);
  const clicksRef = useRef(0);
  const lastInputRef = useRef<number>(Date.now());
  const tabFocusedRef = useRef<boolean>(
    typeof document !== 'undefined' ? !document.hidden : true,
  );

  useEffect(() => {
    if (!enabled) return;

    const onKey = (): void => {
      keysRef.current += 1;
      lastInputRef.current = Date.now();
    };
    const onClick = (): void => {
      clicksRef.current += 1;
      lastInputRef.current = Date.now();
    };
    const onVisibility = (): void => {
      tabFocusedRef.current = !document.hidden;
      if (!document.hidden) lastInputRef.current = Date.now();
    };

    window.addEventListener('keydown', onKey, { passive: true });
    window.addEventListener('click', onClick, { passive: true });
    document.addEventListener('visibilitychange', onVisibility);

    const flush = async (): Promise<void> => {
      const now = Date.now();
      const sinceInputMs = now - lastInputRef.current;
      const focused = tabFocusedRef.current;

      let state: HeartbeatPayload['state'] = 'active';
      if (!focused) state = 'paused';
      else if (screeningId) state = 'documenting';
      if (sinceInputMs > idleThresholdMs) state = 'idle';

      const payload: HeartbeatPayload = {
        state,
        screeningId,
        pageUrl: window.location.pathname,
        keystrokes30s: keysRef.current,
        mouseClicks30s: clicksRef.current,
        tabFocused: focused,
      };
      keysRef.current = 0;
      clicksRef.current = 0;

      try {
        await heartbeatApi.log(payload);
      } catch {
        // best-effort; don't surface to UI
      }
    };

    // Send one immediately so the backend knows we're online, then on interval.
    void flush();
    const id = window.setInterval(() => {
      void flush();
    }, intervalMs);

    return () => {
      window.clearInterval(id);
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('click', onClick);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [enabled, screeningId, intervalMs, idleThresholdMs]);
}
